import { Button, Flex, NumberInput, Stack, Text } from '@mantine/core'
import { IconPlus } from '@tabler/icons-react'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	getListConfigCache,
	incrementConfigCache,
	updateConfigCache,
} from 'src/apis/configCacheAPI'
import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { formatDateTime, size } from 'src/variable/constant'

const CACHE_KEY_LABELS = {
	event_category: 'Event category',
	community_category: 'Community category',
	language_key: 'Language',
	reaction_key: 'Reaction',
	report_issue_key_for_talkroom: 'Talkroom report issues',
	sticker_collection_key: 'Sticker collection',
}

function CacheConfigPage() {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [data, setData] = useState<any[]>([])
	const [loading, setLoading] = useState(false)
	const [values, setValues] = useState<Record<string, number>>({})

	const handleGetData = useCallback(async () => {
		setLoading(true)
		try {
			const res = await getListConfigCache()
			const rows = res?.data?.results?.object || []
			setData(
				rows.map((item, index) => ({
					...item,
					no: index + 1,
					label: CACHE_KEY_LABELS[item.key] || item.key,
					created_at: item.created_at
						? dayjs(item.created_at).format(formatDateTime)
						: '',
					updated_at: item.updated_at
						? dayjs(item.updated_at).format(formatDateTime)
						: '',
				})),
			)
			setValues(
				rows.reduce((acc, item) => {
					acc[item.key] = Number(item.value || 0)
					return acc
				}, {}),
			)
		} catch (error) {
			openError(error)
		} finally {
			setLoading(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleUpdateValue = useCallback(
		async (key: string) => {
			const value = values[key]
			openLoadingContext()
			try {
				const res = await updateConfigCache(key, Number(value || 0))
				if (res?.data?.code === 200) {
					await handleGetData()
					openModal({
						type: 'success',
						label: 'Cache config has been updated successfully',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(values), handleGetData],
	)

	const handleIncrement = useCallback(
		async (key: string) => {
			openLoadingContext()
			try {
				const res = await incrementConfigCache(key)
				if (res?.data?.code === 200) {
					await handleGetData()
					openModal({
						type: 'success',
						label: 'Cache version has been increased successfully',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[handleGetData],
	)

	const columns = useMemo(
		() => [
			{
				accessorKey: 'no',
				header: 'No',
				size: size.zero,
			},
			{
				accessorKey: 'label',
				header: 'Name',
				size: size.medium,
			},
			{
				accessorKey: 'value',
				header: 'Value',
				size: size.text,
				Cell: ({ row }: any) => (
					<NumberInput
						min={0}
						step={1}
						precision={0}
						value={values[row.original.key] ?? 0}
						onChange={(value) =>
							setValues((pre) => ({
								...pre,
								[row.original.key]: Number(value || 0),
							}))
						}
					/>
				),
			},
			{
				accessorKey: 'created_at',
				header: 'Created at',
				size: size.text,
			},
			{
				accessorKey: 'updated_at',
				header: 'Updated at',
				size: size.text,
			},
		],
		[values],
	)

	useEffect(() => {
		handleGetData()
	}, [handleGetData])

	return (
		<Stack sx={{ width: '100%' }}>
			<Text fw={700} size="lg">
				Cache Config
			</Text>
			<TableComponent
				columns={columns}
				total={data.length}
				data={data}
				pagination={{ pageIndex: 1, pageSize: data.length || 10 }}
				loading={loading}
				enableRowActions
				handleChangePage={() => {}}
				_renderCustomActions={(row) => (
					<Flex gap={8}>
						<Button
							variant="outline"
							size="xs"
							leftIcon={<IconPlus size={14} />}
							onClick={() => handleIncrement(row.original.key)}
						>
							+1
						</Button>
						<Button
							variant="outline"
							size="xs"
							onClick={() => handleUpdateValue(row.original.key)}
						>
							Save
						</Button>
					</Flex>
				)}
			/>
		</Stack>
	)
}

export default React.memo(CacheConfigPage)
